import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import {
  downloadSkippedAutomaticMealDebtsCsv,
  fetchSkippedAutomaticMealDebts,
} from '../../controllers/meetingMealController';
import { getCapEventLabel, getSkipReasonLabel, SKIP_REASON_OPTIONS } from './skippedMealDebtsConstants';

const PAGE_SIZE = 20;

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('tr-TR');
};

const SkippedAutomaticMealDebts = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [skipReasonCode, setSkipReasonCode] = useState('');
  const [period, setPeriod] = useState('');
  const [memberId, setMemberId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const query = {
        pageNumber: page,
        pageSize: PAGE_SIZE,
      };
      if (skipReasonCode) query.skipReasonCode = skipReasonCode;
      if (period.trim()) query.period = period.trim();
      if (memberId.trim()) query.memberId = memberId.trim();

      const { data, meta } = await fetchSkippedAutomaticMealDebts(query);
      setItems(Array.isArray(data) ? data : []);
      setTotalCount(meta?.totalCount ?? 0);
    } catch (e) {
      setError(e?.message || 'Liste yüklenemedi');
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, period, skipReasonCode, memberId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const query = { pageNumber: 1, pageSize: 10000 };
      if (skipReasonCode) query.skipReasonCode = skipReasonCode;
      if (period.trim()) query.period = period.trim();
      if (memberId.trim()) query.memberId = memberId.trim();
      await downloadSkippedAutomaticMealDebtsCsv(query);
      toast.success('CSV indirildi');
    } catch (e) {
      toast.error(e?.message || 'CSV indirilemedi');
    } finally {
      setExporting(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Atlanan otomatik yemek borçları
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            Toplantı CAP olaylarında Logo veya cari nedeniyle oluşturulmayan veya Logo fişi atılamayan kayıtlar.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} variant="outlined" onClick={load} disabled={loading}>
            Yenile
          </Button>
          <Button
            startIcon={exporting ? <CircularProgress size={18} /> : <DownloadIcon />}
            variant="contained"
            onClick={handleExport}
            disabled={exporting || loading}
          >
            CSV indir
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 2, border: '1px solid #eee' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            select
            size="small"
            label="Sebep"
            value={skipReasonCode}
            onChange={(e) => { setSkipReasonCode(e.target.value); setPage(1); }}
            sx={{ minWidth: 220 }}
          >
            {SKIP_REASON_OPTIONS.map((o) => (
              <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Dönem (2026-10)"
            value={period}
            onChange={(e) => { setPeriod(e.target.value); setPage(1); }}
          />
          <TextField
            size="small"
            label="Üye ID (GUID)"
            value={memberId}
            onChange={(e) => { setMemberId(e.target.value); setPage(1); }}
            sx={{ flex: 1 }}
          />
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ border: '1px solid #eee' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tarih (UTC)</TableCell>
              <TableCell>Olay</TableCell>
              <TableCell>Üye</TableCell>
              <TableCell>Dönem</TableCell>
              <TableCell>Sebep</TableCell>
              <TableCell>Mesaj</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  Kayıt yok
                </TableCell>
              </TableRow>
            )}
            {!loading && items.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{formatDateTime(row.createdAtUtc)}</TableCell>
                <TableCell>
                  <Tooltip title={row.capEventName}>
                    <span>{getCapEventLabel(row.capEventName)}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Stack spacing={0.25}>
                    <RouterLink to={`/members/${row.memberId}`} style={{ textDecoration: 'none', color: '#0E4D94' }}>
                      {row.memberName || row.memberId}
                    </RouterLink>
                    <Typography variant="caption" color="text.secondary">{row.memberId}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>{row.period || '-'}</TableCell>
                <TableCell>
                  <Chip size="small" label={getSkipReasonLabel(row.skipReasonCode)} />
                </TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Typography variant="body2" noWrap title={row.skipReasonMessage}>
                    {row.skipReasonMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalCount > PAGE_SIZE && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Toplam {totalCount} kayıt
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default SkippedAutomaticMealDebts;
